import React from 'react';
import { Form } from 'components';
import { useIntl } from 'hooks';

interface Value {
  publishers: number[];
  publisherTags: number[];
}

interface Props {
  publishers: { id: number; name: string; image?: string }[];
  publisherTags: { id: number; name: string }[];
  value: Value;
  onChange: (changes: Partial<Value>) => void;
}

const PublishersAndTagsSelect = (props: Props) => {
  const { publishers, publisherTags, value, onChange } = props;

  const { t } = useIntl();

  const options = [
    {
      label: t('PublishersAndTagsSelect__Publishers'),
      options: publishers.map((p) => ({
        value: `publisher-${p.id}`,
        label: p.name,
        image: p.image,
      })),
    },
    {
      label: t('PublishersAndTagsSelect__PublisherTags'),
      options: publisherTags.map((pt) => ({
        value: `publisherTag-${pt.id}`,
        label: pt.name,
      })),
    },
  ];

  // Assuming we get back values like:
  // ['publisher-10', 'publisher-4', 'publisherTag-3', 'publisherTag-7']
  // Split them up so that we can separately update the publishers and
  // publisherTags values.  This allows us to keep publishers and publisherTags
  // in the same Select
  const handleChange = (values: string[]) => {
    const prefixes = {
      publishers: 'publisher-',
      publisherTags: 'publisherTag-',
    };
    const updates: Partial<Value> = {};

    Object.keys(prefixes).forEach((key) => {
      const prefix = prefixes[key];
      const relevantValues = values
        .filter((v) => v.match(prefix))
        .map((v) => parseInt(v.replace(prefix, ''), 10));
      updates[key] = relevantValues;
    });

    onChange(updates);
  };

  // Merge the separate publishers and publisherTags values into one
  const combinedValue = [
    ...value.publishers.map((v) => `publisher-${v}`),
    ...value.publisherTags.map((v) => `publisherTag-${v}`),
  ];

  return (
    <Form.Select
      isMulti
      onChange={handleChange}
      value={combinedValue}
      options={options.filter((option) => !!option.label)}
      isClearable={false}
      placeholder={t('PublishersAndTagsSelect__Placeholder')}
    />
  );
};

export default PublishersAndTagsSelect;
